/**
 * DeleteDialog Component
 * A reusable confirmation dialog for delete operations
 */

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface DeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  title?: string;
  description?: string;
  itemName?: string;
  isLoading?: boolean;
  confirmLabel?: string;
  loadingLabel?: string;
}

export const DeleteDialog = ({
  open,
  onOpenChange,
  onConfirm,
  title = 'Delete Item',
  description = 'Are you sure you want to delete this item? This action cannot be undone.',
  itemName,
  isLoading = false,
  confirmLabel = 'Delete',
  loadingLabel = 'Deleting...'
}: DeleteDialogProps) => {
  const handleConfirm = () => {
    onConfirm();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className='max-w-sm gap-3 rounded-3xl border-gray-500 bg-white/80 p-0 shadow-[0px_3px_12px_0px_rgba(0,0,0,0.08)] backdrop-blur-[6px]'
        showCloseButton={false}
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader className='gap-1.5 px-5 pt-4 pb-0 text-left'>
          <DialogTitle className='text-base font-medium text-ink-strong'>{title}</DialogTitle>
          <DialogDescription className='text-[13px] text-ink'>
            {itemName ? `${description} ${itemName}` : description}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className='px-3 pb-3'>
          <Button variant='outline' onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            className='border-red-500 bg-red-500 text-white hover:bg-red-600'
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {isLoading ? loadingLabel : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
